/**
 * Routing.
 *
 * Every route below is reached from one of the five bottom tabs. The nested
 * routes under `/ca-nhan` and the `/cham-cong` route are pushed screens, not
 * tabs — which is what keeps the navigation to five destinations instead of one
 * long wall of buttons.
 */
import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { LoginScreen } from './auth/LoginScreen';
import { AppShell } from './components/AppShell';
import { useHome, useNotifications } from './lib/queries';
import { useRealtime } from './lib/realtime';
import { HomeScreen } from './screens/HomeScreen';
import { TasksScreen } from './screens/TasksScreen';
import { TaskDetailScreen } from './screens/TaskDetailScreen';
import { NotificationsScreen } from './screens/NotificationsScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { AttendanceScreen } from './screens/AttendanceScreen';
import { AttendanceHistoryScreen } from './screens/AttendanceHistoryScreen';
import { ShiftScheduleScreen } from './screens/ShiftScheduleScreen';
import { LeaveRequestScreen } from './screens/LeaveRequestScreen';
import { ShiftSwapScreen } from './screens/ShiftSwapScreen';
import { SalaryAdvanceScreen } from './screens/SalaryAdvanceScreen';
import { PayrollPreviewScreen } from './screens/PayrollPreviewScreen';
import { KpiDetailScreen } from './screens/KpiDetailScreen';
import { AppealScreen } from './screens/AppealScreen';
import { ChatScreen } from './screens/ChatScreen';
import { LoadingState } from './components/ui';

/**
 * The QR scanner is the only lazy route.
 *
 * `@zxing/library` is ~500 kB of the bundle on its own. Loading it eagerly
 * would mean every employee pays for the decoder on first launch — over a
 * restaurant's wifi, on a cheap Android — even on the shifts where they never
 * scan anything. Splitting it costs one spinner the first time the tab is
 * opened, and the service worker precaches the chunk right afterwards.
 */
const ScanScreen = lazy(() =>
  import('./screens/ScanScreen').then((module) => ({ default: module.ScanScreen })),
);

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AuthenticatedApp />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AuthenticatedApp(): JSX.Element {
  const { me } = useAuth();
  if (me === null) return <LoginScreen />;
  return <SignedInRoutes />;
}

function SignedInRoutes(): JSX.Element {
  // The home payload already carries both badge counts, and it is polling
  // anyway — so the nav badges cost nothing extra.
  const home = useHome();
  // The BranchHub socket (or its polling fallback) — makes a dispatch offer
  // feel instant instead of arriving on the next 10s poll.
  useRealtime();
  // The notification badge prefers the real inbox (same cache the Thông báo
  // screen renders, so tapping "đã đọc" updates the badge immediately) and
  // falls back to the count /staff/home already carries.
  const notifications = useNotifications();
  const unreadFromList =
    notifications.data === undefined
      ? null
      : notifications.data.notifications.filter((n) => n.readAt === null).length;
  const badges = {
    notifications: unreadFromList ?? home.data?.unreadNotifications ?? 0,
    tasks: home.data?.offeredTaskCount ?? 0,
  };

  return (
    <Routes>
      <Route element={<AppShell badges={badges} />}>
        {/* --- the five tabs ------------------------------------------- */}
        <Route index element={<HomeScreen />} />
        <Route path="/cong-viec" element={<TasksScreen />} />
        <Route
          path="/quet-qr"
          element={
            <Suspense fallback={<LoadingState label="Đang tải bộ quét mã…" />}>
              <ScanScreen />
            </Suspense>
          }
        />
        <Route path="/thong-bao" element={<NotificationsScreen />} />
        <Route path="/ca-nhan" element={<ProfileScreen />} />

        {/* --- pushed screens ------------------------------------------ */}
        <Route path="/cong-viec/:taskId" element={<TaskDetailScreen />} />
        <Route path="/cham-cong" element={<AttendanceScreen />} />
        <Route path="/ca-nhan/lich-su-cham-cong" element={<AttendanceHistoryScreen />} />
        <Route path="/ca-nhan/lich-ca" element={<ShiftScheduleScreen />} />
        <Route path="/ca-nhan/xin-nghi" element={<LeaveRequestScreen />} />
        <Route path="/ca-nhan/doi-ca" element={<ShiftSwapScreen />} />
        <Route path="/ca-nhan/ung-luong" element={<SalaryAdvanceScreen />} />
        <Route path="/ca-nhan/luong-tam-tinh" element={<PayrollPreviewScreen />} />
        <Route path="/ca-nhan/kpi" element={<KpiDetailScreen />} />
        <Route path="/ca-nhan/khieu-nai" element={<AppealScreen />} />
        <Route path="/ca-nhan/chat" element={<ChatScreen />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
